// „მოარგე ეკრანი" — the first time MartePOS opens on a device (2026-10-09).
//
// The owner: a new user meets a stretched screen („სრულიად გაწელილი ვიზუალი")
// because `auto` density cannot tell a 14" laptop at 100 % from a 24" monitor —
// both report 1920 px. So on a NEW device a small window asks, once, before
// anything else (components/screen-fit-dialog.tsx). Spec:
// docs/superpowers/specs/2026-10-09-screen-fit-first-run-design.md.
//
// „New" is decided ONCE, by index.html's boot script, before any module can
// write storage: a device holding one of MartePOS's own keys has run it before
// and never sees the window — so no till in use today gets it after the
// release. ⚠️ The key and the list are DUPLICATED in index.html (it runs before
// any module); lib/screen-fit.test.ts + lib/screen-fit-boot.test.ts keep them equal.

export const SCREEN_FIT_KEY = 'screenFit'

export type ScreenFitState = 'pending' | 'done'

/** MartePOS's own keys — any one of them means the device ran it before. */
export const LEGACY_KEYS = ['pos-ui-prefs', 'theme', 'density', 'lang', 'touchKeyboard', 'pos-active-branch'] as const

export function firstDecision(has: (key: string) => boolean): ScreenFitState {
  return LEGACY_KEYS.some((k) => has(k)) ? 'done' : 'pending'
}

/** Only an explicit „pending" offers the window; missing, anything else or a blocked storage = done. */
export function readScreenFit(storage: Pick<Storage, 'getItem'> = localStorage): ScreenFitState {
  try {
    return storage.getItem(SCREEN_FIT_KEY) === 'pending' ? 'pending' : 'done'
  } catch {
    return 'done'
  }
}

export function finishScreenFit(storage: Pick<Storage, 'setItem'> = localStorage): void {
  try { storage.setItem(SCREEN_FIT_KEY, 'done') } catch { /* private mode: this session only */ }
}

/** Spec §2: a new device, at least 768 px wide, past the PIN, not a support
 *  session, not kiosk mode, not the handheld /terminal. */
export function shouldOfferScreenFit(o: {
  state: ScreenFitState
  wide: boolean
  path: string
  support: boolean
  locked: boolean
  kiosk: boolean
}): boolean {
  return o.state === 'pending' && o.wide && !o.locked && !o.support && !o.kiosk && !o.path.startsWith('/terminal')
}

/** The keyboard question is asked only where a finger can press the screen. */
export function isTouchScreen(o: { coarse: boolean; maxTouchPoints: number }): boolean {
  return o.coarse || o.maxTouchPoints > 0
}
